import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import heroImage from "@/assets/hero-racing.webp";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

// Product images for floating background
const productImages = [
  "/products/bmw-m4-csl-tee.jpg",
  "/products/eyes-dont-lie-tee.jpg",
  "/products/gtr-tee.jpg",
  "/products/hot-girls-club-tee.jpg",
  "/products/lbwk-supra-hoodie.jpg",
  "/products/lbwk-supra-tee.jpg",
  "/products/look-mom-sv-hoodie.jpg",
  "/products/look-mom-sv-tee.jpg",
  "/products/outlaws-hoodie.jpg",
  "/products/sinc-hoodie.jpg",
];

// Event album images for collage
const eventImages = [
  "/lovable-uploads/event-1.webp",
  "/lovable-uploads/event-2.webp",
  "/lovable-uploads/event-3.webp",
  "/lovable-uploads/event-4.webp",
  "/lovable-uploads/event-5.webp",
  "/lovable-uploads/event-6.webp",
];

interface Slide {
  id: number;
  type: "hero" | "event-albums" | "products";
  backgroundImage?: string;
  title?: string;
  subtitle?: string;
  cta?: { text: string; href: string };
  label?: string;
  link?: string;
  buttonText?: string;
  buttonLink?: string;
}

const slides: Slide[] = [
  {
    id: 1,
    type: "hero",
    backgroundImage: heroImage,
    title: "Welcome To Øutlaws",
    subtitle: "A brand built for domination",
    cta: { text: "[ SHOP NOW ]", href: "/apparel" }
  },
  {
    id: 2,
    type: "event-albums",
    label: "Event Albums",
    link: "/event-albums",
    buttonText: "View Event Images",
    buttonLink: "/event-albums"
  },
  {
    id: 3,
    type: "event-albums",
    label: "Past Events",
    link: "/events",
    buttonText: "View Past Events",
    buttonLink: "/events"
  },
];

// Enhanced Collage component for event album slides - larger and more visually prominent
const EventCollage = ({ images }: { images: string[] }) => (
  <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-0.5">
    {images.slice(0, 6).map((img, i) => (
      <div key={i} className="relative overflow-hidden">
        <img
          src={img}
          alt={`Event highlight ${i + 1}`}
          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
          loading="lazy"
        />
        {/* Subtle overlay per image for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/30 to-transparent" />
      </div>
    ))}
    {/* Overall gradient overlay for text readability */}
    <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/70 to-background/90" />
  </div>
);

// Floating product images background
const FloatingProducts = ({ images }: { images: string[] }) => {
  const positions = [
    { top: '5%', left: '8%', size: 'w-28 h-36 md:w-36 md:h-44', delay: '0s', duration: '18s', rotate: '-12deg' },
    { top: '15%', left: '65%', size: 'w-32 h-40 md:w-40 md:h-48', delay: '2s', duration: '22s', rotate: '8deg' },
    { top: '55%', left: '5%', size: 'w-24 h-32 md:w-32 md:h-40', delay: '4s', duration: '20s', rotate: '15deg' },
    { top: '60%', left: '75%', size: 'w-28 h-36 md:w-36 md:h-44', delay: '1s', duration: '24s', rotate: '-8deg' },
    { top: '30%', left: '35%', size: 'w-24 h-32 md:w-28 md:h-36', delay: '3s', duration: '19s', rotate: '6deg' },
    { top: '10%', left: '85%', size: 'w-20 h-28 md:w-28 md:h-36', delay: '5s', duration: '21s', rotate: '-18deg' },
    { top: '75%', left: '40%', size: 'w-24 h-32 md:w-32 md:h-40', delay: '2.5s', duration: '23s', rotate: '10deg' },
    { top: '40%', left: '15%', size: 'w-20 h-28 md:w-24 md:h-32', delay: '6s', duration: '17s', rotate: '-5deg' },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden">
      {positions.map((pos, i) => (
        <div
          key={i}
          className={`absolute ${pos.size} rounded-sm overflow-hidden shadow-xl opacity-40`}
          style={{
            top: pos.top,
            left: pos.left,
            transform: `rotate(${pos.rotate})`,
            animation: `floatProduct ${pos.duration} ease-in-out ${pos.delay} infinite`,
          }}
        >
          <img
            src={images[i % images.length]}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/70 to-background/85" />
    </div>
  );
};

export const HeroSlideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 4000);

    return () => clearInterval(timer);
  }, [nextSlide]);

  const renderSlide = (slide: Slide, isActive: boolean) => {
    if (slide.type === "hero") {
      return (
        <div className="relative h-full flex flex-col items-center justify-center">
          <img
            src={slide.backgroundImage}
            alt="Øutlaws hero"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background/60" />
          <div className="relative z-10 text-center px-6 animate-fade-in">
            <h1 className="section-title text-foreground mb-4">
              {slide.title}
            </h1>
            <p className="text-foreground/80 mb-8 tracking-wide font-serif text-2xl">
              {slide.subtitle}
            </p>
            <Link to={slide.cta?.href || "/"} className="btn-racing">
              {slide.cta?.text}
            </Link>
          </div>
        </div>
      );
    }

    if (slide.type === "event-albums") {
      return (
        <div className="relative h-full flex flex-col items-center justify-center bg-background overflow-hidden">
          {/* Enhanced Event collage background - more visible */}
          <EventCollage images={eventImages} />
          
          <div className="relative z-10 text-center px-6">
            <Link
              to={slide.link || "/"}
              className="group cursor-pointer inline-block"
            >
              <h2
                className={`text-5xl md:text-7xl lg:text-8xl font-display italic font-bold text-primary 
                  transition-all duration-300 hover:scale-105 drop-shadow-lg
                  ${isActive ? "animate-pop-in" : "opacity-0"}`}
                style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
              >
                {slide.label}
              </h2>
              <div className="mt-4 text-center">
                <span className="text-foreground/80 text-sm tracking-widest uppercase group-hover:text-primary transition-colors font-medium drop-shadow-md">
                  Click to explore →
                </span>
              </div>
            </Link>
            
            {/* CTA Button */}
            {slide.buttonText && slide.buttonLink && (
              <div className={`mt-8 ${isActive ? "animate-fade-in" : "opacity-0"}`} style={{ animationDelay: "0.3s" }}>
                <Button
                  asChild
                  className="btn-racing-filled text-lg px-10 py-7 font-display italic tracking-wide shadow-xl hover:shadow-2xl transition-shadow"
                >
                  <Link to={slide.buttonLink}>
                    {slide.buttonText}
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (slide.type === "products") {
      return (
        <div className="relative h-full flex flex-col items-center justify-center bg-background overflow-hidden">
          {/* Floating product images background */}
          <FloatingProducts images={productImages} />
          <Link
            to={slide.link || "/"}
            className="relative z-10 group cursor-pointer"
          >
            <h2
              className={`text-5xl md:text-7xl lg:text-8xl font-display italic font-bold text-primary 
                transition-all duration-300 hover:scale-105 drop-shadow-lg
                ${isActive ? "animate-pop-in" : "opacity-0"}`}
              style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
            >
              {slide.label}
            </h2>
            <div className="mt-4 text-center">
              <span className="text-foreground/60 text-sm tracking-widest uppercase group-hover:text-primary transition-colors drop-shadow-md">
                Click to explore →
              </span>
            </div>
          </Link>
        </div>
      );
    }

    return null;
  };

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-500 ease-in-out
            ${index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"}`}
        >
          {renderSlide(slide, index === currentSlide)}
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 text-foreground/50 hover:text-foreground transition-colors"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-10 h-10" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 text-foreground/50 hover:text-foreground transition-colors"
        aria-label="Next slide"
      >
        <ChevronRight className="w-10 h-10" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-1">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="flex items-center justify-center min-w-[44px] min-h-[44px]"
            aria-label={`Go to slide ${index + 1}`}
          >
            <span className={`block rounded-full transition-all duration-300 h-2 ${
              index === currentSlide
                ? "bg-primary w-8"
                : "bg-foreground/30 hover:bg-foreground/50 w-2"
            }`} />
          </button>
        ))}
      </div>
    </section>
  );
};
